import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { FondDeProjet, FormeDeProjet, TraitDeProjet } from '@nextilt/commun';
import { signalerCeQueGrossitLaCarte, signalerLesOuvertes } from '../aide';
import { useCarteDepliee } from '../partage/useArbre';
import type {
  Bulle as TypeBulle,
  BulleEnrichie,
  CarteDeFeuille,
  Decompte,
  Etat,
  Priorite,
} from '../partage/api/types';
import { BarreDeLaCarte } from './BarreDeLaCarte';
import { contourDesBoites } from './borneDeLaCarte';
import { dansLeCalque } from './calque';
import { couleurDAxe } from './couleurDAxe';
import { useCameraDeCarte } from './cameraCarte';
import { demandeDeLaCarte } from './demandeDeLaCarte';
import { peutSeRemettreASaPlace, placesGlissees } from './deplacerUneBulle';
import { useGlisserUneBulle } from './useGlisserUneBulle';
import { useRemettreASaPlace } from './useRemettreASaPlace';
import { gestesDuCentre } from '../partage/deuxGestes';
import { compterLeFiltre, filtreEnvoye, tailleAuFiltre, type FiltreDeLaCarte } from './filtreDeLaCarte';
import { useGestesDeDepliage } from './gestesDeDepliage';
import { EtiquettesTransversales } from './EtiquettesTransversales';
import { NoeudCarte } from './NoeudCarte';
import type { ProfondeurIci } from './profondeurDuDossier';
import { TraitsCarte, ZonesDesLiens } from './TraitsCarte';
import { liensMontres } from './dessinDesLiens';
import { SignesDuLien, type LienDesigne } from './SignesDuLien';
import { BOITE_DU_CENTRE, boitesDesBulles } from './placeDesEtiquettes';
import { dessinDeLaVue, hauteursAuMoins, placeDe, tailleDeLaVue } from './planDeLaCarte';
import { useTaillesDesBulles } from './taillesDesBulles';
import { colonnesDeLaCarte, type ColonneDeLaCarte } from './colonnesDeLaCarte';
import { faitesSurN, GESTE_DU_CENTRE, phraseDuFiltre, RIEN_DEDANS } from '../partage/vocabulaire';
import { teinteOuAccent } from '../apparence';
import type { BulleAHabiller, HabitDessine } from '../partage/objetsDeBulle';
import type { HabitDuProjetPourUneBulle } from '../fiche';
import { AjoutDePapiers, boitesDesPapiers, papiersSeMontrent, PapiersSurLaCarte, usePapiers } from '../papiers';
import { loupeSurLaBulle, SANS_LOUPE, type LoupeDeLaCarte } from '../partage/loupeDeLaCarte';
import './VueTableau.css';

/** Poser un post-it ou une liste d'une touche (P, L) : ce que la carte donne à la feuille. */
export interface PoseDesPapiers {
  postIt: () => void;
  liste: () => void;
}

/**
 * Regrouper (chantier theme-analyse, ticket 03) : en Analyse, les bulles en colonnes, une par groupe.
 * `cle` : le regroupement (état, contact, lieu, mois), qui recadre quand il change.
 */
export interface RegroupementDeLaCarte {
  cle: string;
  colonnes: readonly ColonneDeLaCarte[];
}

/** Pendant « Lier », ni fiche, ni état, ni dépliage : le geste ne fait rien. */
const rienAFaire = () => {};

/** Pas de papier sur la carte : une seule liste vide, pour que la borne du regard ne se recalcule pas. */
const AUCUN_PAPIER: readonly never[] = [];
/** Autour des colonnes, de quoi respirer : la caméra s'en sert pour savoir jusqu'où dézoomer. */
const MARGE_DES_COLONNES = 100;

/**
 * La carte : design/carte.html, ouvrir une étape sur place compris (ticket 06 de
 * retours-testeurs, qui remplace le recul niveau par niveau du ticket 36).
 *
 * **Ce n'est pas le canvas que la SPEC repoussait** (principe 8) : la position d'une bulle est
 * calculée par le serveur ; on glisse le REGARD pour se déplacer. Seule exception, cadenas ouvert
 * (chantier deplacer-les-bulles, ticket 03, ADR 0016) : glisser une bulle la déplace, et le
 * serveur garde son décalage par rapport à sa mère, pour la profondeur affichée
 * (`useGlisserUneBulle`) ; il range le reste autour, rien ne l'exige. Seuls les papiers posés sur la carte (post-it, listes libres,
 * chantier post-it-et-listes, ADR 0014) gardent leur place et leur taille : sur la carte
 * repliée de la bulle au centre (profondeur 1, rien d'ouvert sur place), ils se posent sous les
 * traits et les bulles et suivent le regard (`../papiers`). Ils se glissent, et leur poignée règle
 * leur taille (ticket 03) ; glisser un papier ne bouge ni le regard, ni une bulle. Ils vont où
 * on les lâche, et la borne du regard les englobe : on glisse toujours la carte jusqu'à eux.
 *
 * Pourquoi elle existe : *« la liste tolère l'empilement plat, et un empilement plat n'a ni
 * condition, ni chaîne, ni profondeur — c'est-à-dire rien à calculer »* (SPEC §10). Autour
 * d'un centre, on voit ce qu'on demande, on regroupe, et la profondeur se produit **sans un
 * seul avertissement**. Une étape s'ouvre sur place, à la molette ou par sa petite flèche :
 * ses étapes l'entourent, serrées, la caméra se centre sur elle, et seules les voisines
 * qu'elles gênent se décalent (ticket 12). Chaque axe a sa couleur, qui dit à qui est une
 * bulle.
 *
 * Depuis le ticket 04 du chantier `priorites`, plus de coins ni de noms de part : les bulles
 * se rangent autour du centre dans ton ordre. Depuis le chantier `revue-design` (ticket 04,
 * maquette `design/carte.html`), le filtre en pastilles (`BarreDeLaCarte`) remplace le calque
 * de ⚙ : aux profondeurs 2 et 3, ce qui porte l'importance choisie grossit et le reste
 * rétrécit, placé par le serveur à ces tailles (`filtreDeLaCarte.ts`) ; rien ne s'ombre plus.
 * Depuis le chantier `tete-du-projet` (modification 01), ce filtre est caché, pas supprimé.
 * Le centre dit l'avancement (« 4 sur 119 faites »).
 *
 * Ce fichier ne dessine que le PLAN : quelles bulles sont visibles, où chaque chose se pose,
 * et l'ordre des couches : traits, centre, nœuds. Chaque couche sait
 * se dessiner toute seule ; la caméra vit dans `cameraCarte.ts`, ce qui est ouvert dans
 * `profondeur.ts` (le sélecteur de profondeur et la molette, ticket 13), tenu par la feuille.
 */
export function VueTableau({
  centre,
  enfants,
  carte,
  enAvant,
  onFiltrer,
  decompte,
  onRecentrer,
  couleur,
  fond,
  forme,
  trait,
  habitDe,
  habitDuProjet,
  hierarchisation,
  ouverteId,
  profondeur,
  onDescendre,
  onFiche,
  onEtat,
  loupe = SANS_LOUPE,
  poseDesPapiers,
  regroupe = null,
  liensCachesMontres = false,
  lier = null,
  onFicheDAilleurs,
  cadenasOuvert = false,
}: {
  centre: TypeBulle;
  enfants: BulleEnrichie[];
  carte: CarteDeFeuille;
  /** L'importance mise en avant par le filtre, ou aucune (`useCalque`). */
  enAvant: Priorite | null;
  onFiltrer: (enAvant: Priorite | null) => void;
  /** « N sur M » de la feuille, compté par le serveur. */
  decompte: Decompte;
  /** Donne à la feuille le geste « Recentrer » de la carte (`null` en partant), pour le menu de Profondeur. */
  onRecentrer?: (recentrer: (() => void) | null) => void;
  couleur: string | null;
  /** Le fond de la carte, celui de l'habit du projet (Uni : le fond de l'ambiance). */
  fond: FondDeProjet;
  /** La forme des bulles, celle de l'habit du projet ; aucune au téléphone (`formeDessinee`). */
  forme: FormeDeProjet | undefined;
  /** Le trait de leur contour, celui de l'habit du projet ; aucun au téléphone (`traitDessine`). */
  trait: TraitDeProjet | undefined;
  /**
   * L'habit de chaque bulle, par-dessus celui du projet (chantier habit-des-bulles, modification
   * 03, `habitsDessines`) : sa forme ou son objet, son trait, la couleur choisie de sa branche.
   */
  habitDe: (bulle: BulleAHabiller) => HabitDessine;
  /** L'habit du projet, pour le menu « ⋯ » de chaque bulle (habit-des-bulles, 05) ; `null` au téléphone. */
  habitDuProjet: HabitDuProjetPourUneBulle | null;
  /** En mode « hiérarchiser », on tape 1, 2, 3 : la carte montre alors les rangs en attente. */
  hierarchisation: Map<string, number> | null;
  /** La branche d'où l'on vient de remonter : c'est elle que la planche dessine « ouverte ». */
  ouverteId: string | null;
  /** La profondeur et ce qui est ouvert sur place, retenus tant qu'on reste dans le dossier. */
  profondeur: ProfondeurIci;
  onDescendre: (id: string) => void;
  /** Une bulle dépliée comprise : sa fiche trouve son fil dans ce qui est lu (`laFicheOuverte`). */
  onFiche: (id: string) => void;
  onEtat: (id: string, etat: Etat) => void;
  /** Les touches de la carte (chantier modes-de-la-carte, modification 02) : infos et recherche. */
  loupe?: LoupeDeLaCarte;
  /**
   * P et L (modes-de-la-carte, 02) : la carte y range « + Post-it » et « + Liste » tant qu'ils se
   * montrent, `null` sinon ; la feuille les appelle d'une touche.
   */
  poseDesPapiers?: MutableRefObject<PoseDesPapiers | null>;
  /**
   * Regrouper (theme-analyse, ticket 03) : les colonnes remplacent les places du serveur, à l'écran
   * seulement ; la mère, les traits, les étiquettes et les papiers se cachent, la caméra recadre.
   * `null` : la carte d'avant, exactement.
   */
  regroupe?: RegroupementDeLaCarte | null;
  /**
   * La touche A (chantier liens-entre-bulles, ticket 03) : les liens cachés se dessinent aussi, en
   * pâle, avec leurs étiquettes, et se cliquent pour être montrés.
   */
  liensCachesMontres?: boolean;
  /**
   * « Lier » en cours (chantier liens-entre-bulles, ticket 04) : un clic sur une bulle va à « Lier »,
   * la première est marquée ; ni fiche, ni état, ni dépliage, ni signes d'un trait, ni papiers,
   * comme pendant Hiérarchiser. `null` : la carte d'avant.
   */
  lier?: { premiere: string | null; onClic: (id: string) => void } | null;
  /**
   * Un clic sur l'étiquette d'un lien vers une bulle hors de la feuille : sa fiche, par-dessus la
   * carte (chantier liens-entre-bulles, ticket 05). Pas pendant Hiérarchiser ni Lier.
   */
  onFicheDAilleurs?: (id: string) => void;
  /**
   * Le cadenas du bord droit ouvert (chantier deplacer-les-bulles, ticket 03, ADR 0016) : glisser
   * une bulle la déplace, avec ce qu'elle porte, et sa place est gardée pour la profondeur affichée.
   * Fermé : la carte d'avant, glisser une bulle bouge le regard.
   */
  cadenasOuvert?: boolean;
}) {
  const bulles = useMemo(() => [...enfants, ...carte.dessous], [enfants, carte.dessous]);
  const parId = useMemo(() => new Map(bulles.map((b) => [b.id, b])), [bulles]);
  const enfantsDe = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const b of bulles) {
      if (b.parentId) m.set(b.parentId, [...(m.get(b.parentId) ?? []), b.id]);
    }
    return m;
  }, [bulles]);

  /*
   * Ce qui est ouvert ICI (ticket 13 : le socle de la profondeur, puis la molette), dans
   * l'ordre que le serveur respecte. Tout replié, la feuille porte déjà sa carte ; sinon on
   * demande les places au serveur, et la carte d'avant reste à l'écran le temps qu'elles
   * arrivent. Le centre n'est jamais « ouvert » : on est entré dedans.
   *
   * Avec un filtre, à partir de la profondeur 2, le serveur place les bulles à leur taille du
   * filtre (`filtreDeLaCarte.ts`) : on lui demande alors les places même tout replié.
   */
  const ouvertes = profondeur.ouvertes;
  const filtre = useMemo(() => filtreEnvoye(enAvant, profondeur.actuelle), [enAvant, profondeur.actuelle]);
  /*
   * La profondeur affichée part toujours avec la demande (chantier deplacer-les-bulles, ticket 03) :
   * le serveur y lit les places gardées. À la profondeur 1, tout replié et sans filtre, la feuille
   * porte déjà sa carte, lue à la profondeur 1.
   */
  const demande = useMemo(
    () => demandeDeLaCarte(ouvertes, profondeur.actuelle, enAvant),
    [ouvertes, profondeur.actuelle, enAvant]
  );
  const demandee = demande !== null;
  const depliee = useCarteDepliee(centre.id, demande);
  const vue = demandee ? (depliee.data ?? carte.vue) : carte.vue;
  const ouvertesIci = useMemo(() => new Set(ouvertes), [ouvertes]);
  /*
   * Ce que le serveur a vraiment ouvert, une fois ses places arrivées : au plafond de bulles
   * visibles, une étape demandée ne s'ouvre pas, et sa flèche ne doit pas le prétendre.
   */
  const placesFraiches = demandee && !!depliee.data && !depliee.isPlaceholderData;
  /*
   * Les tailles suivent les places à l'écran, pas le filtre demandé : le temps que les places
   * du nouveau filtre arrivent, les bulles gardent la taille avec laquelle les places montrées
   * ont été calculées, sans quoi elles se poseraient un instant les unes sur les autres.
   */
  const filtreDesPlaces = useRef<FiltreDeLaCarte | null>(null);
  if (!demandee) filtreDesPlaces.current = null;
  else if (placesFraiches) filtreDesPlaces.current = filtre;
  const filtreDessine = filtreDesPlaces.current;
  const tailleDe = useCallback(
    (bulle: BulleEnrichie) => tailleAuFiltre(dansLeCalque(bulle, enAvant, carte.calque).allumee, filtreDessine),
    [enAvant, carte.calque, filtreDessine]
  );
  const deplieesMontrees = useMemo(
    () => (placesFraiches ? new Set(vue.depliees) : ouvertesIci),
    [placesFraiches, vue.depliees, ouvertesIci]
  );

  /*
   * Une bulle se montre si elle est au premier tour, ou si toutes les étapes qui la
   * contiennent sont ouvertes. Le temps que les places arrivent, une étape qu'on vient de
   * refermer rentre donc tout de suite, et celle qu'on ouvre attend ses places sur sa mère.
   */
  const placesDuServeur = useMemo(() => {
    const montrable = (id: string) => {
      for (let p = parId.get(id)?.parentId; p && p !== centre.id; p = parId.get(p)?.parentId) {
        if (!ouvertesIci.has(p)) return false;
      }
      return true;
    };
    return Object.fromEntries(Object.entries(vue.places).filter(([id]) => montrable(id)));
  }, [vue.places, parId, centre.id, ouvertesIci]);

  // La taille de chaque bulle à l'écran (ticket 25) : les traits s'arrêtent à son bord, et
  // une étiquette l'évite même quand le titre prend plus de place que le serveur ne l'a cru.
  const [monde, setMonde] = useState<HTMLDivElement | null>(null);
  const mesureesAPlat = useTaillesDesBulles(
    monde,
    useMemo(() => bulles.map((b) => b.id).join(' '), [bulles])
  );
  // La mesure ignore l'échelle de la bulle : le filtre la grossit ou la rétrécit à l'écran.
  const mesurees = useMemo(() => {
    if (filtreDessine === null) return mesureesAPlat;
    const parTaille: typeof mesureesAPlat = {};
    for (const [id, t] of Object.entries(mesureesAPlat)) {
      const b = parId.get(id);
      const k = b ? tailleDe(b) : 1;
      parTaille[id] = { largeur: t.largeur * k, hauteur: t.hauteur * k };
    }
    return parTaille;
  }, [mesureesAPlat, filtreDessine, parId, tailleDe]);
  const encombrement = useMemo(
    () => ({ hauteurs: hauteursAuMoins(carte.hauteurs, mesurees) }),
    [carte.hauteurs, mesurees]
  );
  // Un lien caché ne se dessine pas, ni son étiquette, sauf avec la touche A.
  const vueMontree = useMemo(
    () => ({ ...vue, places: placesDuServeur, dehors: liensMontres(vue.dehors, liensCachesMontres) }),
    [vue, placesDuServeur, liensCachesMontres]
  );
  // Les étiquettes des places du serveur : la taille de la carte et la borne du regard s'y tiennent,
  // sans bouger pendant qu'on glisse une bulle.
  const { etiquettes: etiquettesFixes } = useMemo(
    () => dessinDeLaVue(vueMontree, encombrement),
    [vueMontree, encombrement]
  );

  /*
   * Regrouper : les colonnes, calculées ici depuis les groupes (`colonnesDeLaCarte.ts`), prennent la
   * place des places du serveur pour tout ce qui suit : ce qui se voit, où chaque bulle se pose, la
   * borne du regard, la caméra. Les colonnes portent les bulles que la carte montre à la profondeur
   * choisie (ticket 03 rouvert, choix A d'Igor) ; ce qui est plus profond se cache sur sa mère.
   * Rien n'est stocké.
   */
  const enColonnes = useMemo(
    // Une feuille sans étape n'a rien à ranger : la carte d'avant reste, avec sa place à prendre.
    () => (regroupe && regroupe.colonnes.length > 0 ? colonnesDeLaCarte(regroupe.colonnes, encombrement.hauteurs) : null),
    [regroupe, encombrement.hauteurs]
  );
  /** Les places sans le glisser d'une bulle : la taille, la borne du regard, ce qui se voit. */
  const placesFixes = enColonnes?.places ?? placesDuServeur;
  const visibles = useMemo(() => new Set(Object.keys(placesFixes)), [placesFixes]);
  const contourDesColonnes = useMemo(() => (enColonnes ? contourDesBoites(enColonnes.boites) : null), [enColonnes]);
  const taille = useMemo(
    () =>
      contourDesColonnes
        ? {
            largeur: contourDesColonnes.droite - contourDesColonnes.gauche + 2 * MARGE_DES_COLONNES,
            hauteur: contourDesColonnes.bas - contourDesColonnes.haut + 2 * MARGE_DES_COLONNES,
          }
        : tailleDeLaVue(vueMontree, { etiquettes: etiquettesFixes }, enfants.length === 0),
    [contourDesColonnes, vueMontree, etiquettesFixes, enfants.length]
  );

  const liensVus = useMemo(
    () => liensMontres(vue.liens, liensCachesMontres).filter((l) => visibles.has(l.sourceId) && visibles.has(l.cibleId)),
    [vue.liens, liensCachesMontres, visibles]
  );
  /** Le lien désigné par un clic sur son trait : ses signes s'ouvrent au milieu (ticket 03). */
  const [designe, setDesigne] = useState<LienDesigne | null>(null);
  const fermerLesSignes = useCallback(() => setDesigne(null), []);
  const depliable = useCallback((id: string) => enfantsDe.has(id), [enfantsDe]);
  const enHierarchisation = hierarchisation !== null;
  const enLiaison = lier !== null;
  // Hiérarchiser ou Lier : un clic sur une bulle la désigne, et rien d'autre ne se fait.
  const enDesignation = enHierarchisation || enLiaison;
  // Les signes d'un trait ouverts se ferment quand « Lier » commence.
  useEffect(() => {
    if (enLiaison) setDesigne(null);
  }, [enLiaison]);

  // La molette passe d'abord par les gestes de dépliage ; ce qu'ils laissent, la caméra le zoome.
  const molette = useRef<(evenement: WheelEvent) => boolean>(() => false);
  // Les papiers de la bulle au centre, sur la carte repliée seulement (décision d'Igor du 2026-10-19).
  // En colonnes, les papiers se cachent (maquette `regroupe`) : ils gardent leur place, pour après.
  const avecPapiers = papiersSeMontrent(profondeur.actuelle, ouvertes.length) && !enColonnes;
  const papiers = usePapiers(centre.id, avecPapiers);
  // Le bord des bulles visibles, étapes ouvertes comprises, et des papiers visibles : il ne
  // quitte jamais tout à fait la surface (ticket 05 de retours-testeurs). Les papiers en font
  // partie pour qu'on puisse toujours glisser la carte jusqu'à un papier rangé loin (retour
  // d'Igor du 2026-10-20 sur le ticket 03 de post-it-et-listes) ; sans papier, rien ne change.
  // C'est leur place gardée qui compte, pas celle d'un papier qu'on tire encore : le regard ne
  // bouge pas pendant le geste.
  const papiersMontres = avecPapiers ? papiers.papiers : AUCUN_PAPIER;
  const contour = useMemo(
    () =>
      contourDesColonnes ??
      contourDesBoites([
        BOITE_DU_CENTRE,
        ...boitesDesBulles(placesFixes, encombrement.hauteurs),
        ...boitesDesPapiers(papiersMontres),
      ]),
    [contourDesColonnes, placesFixes, encombrement.hauteurs, papiersMontres]
  );
  const cadrage = useMemo(
    () => (regroupe && contourDesColonnes ? { cle: regroupe.cle, contour: contourDesColonnes } : null),
    [regroupe, contourDesColonnes]
  );
  const camera = useCameraDeCarte(taille, contour, (evenement) => molette.current(evenement), cadrage);

  /*
   * Glisser une bulle, cadenas ouvert (chantier deplacer-les-bulles, ticket 03) : pendant le geste,
   * elle et ce qu'elle porte suivent la main, et restent où on les a lâchées jusqu'à la carte
   * relue ; le serveur range le reste. Ni pendant Lier ou Hiérarchiser, ni en colonnes.
   */
  const glisser = useGlisserUneBulle({
    cadenasOuvert,
    enDesignation,
    enColonnes: enColonnes !== null,
    profondeur: profondeur.actuelle,
    centreId: centre.id,
    places: placesDuServeur,
    parId,
    enfantsDe,
    versLaCarte: camera.versLaCarte,
  });
  const { glissee } = glisser;
  // « Remettre à sa place » dans le « ⋯ » d'une bulle fixée à la profondeur affichée (ticket 04).
  const remettreASaPlace = useRemettreASaPlace(profondeur.actuelle);
  const placesVues = useMemo(
    () => (glissee ? placesGlissees(placesDuServeur, glissee.emportees, glissee.ecart) : placesDuServeur),
    [glissee, placesDuServeur]
  );
  const places = enColonnes?.places ?? placesVues;
  const etiquettes = useMemo(
    () => (glissee ? dessinDeLaVue({ ...vueMontree, places: placesVues }, encombrement).etiquettes : etiquettesFixes),
    [glissee, vueMontree, placesVues, encombrement, etiquettesFixes]
  );
  const ouEst = useCallback(
    (id: string) => placeDe(id, places, parId, centre.id),
    [places, parId, centre.id]
  );
  // « Recentrer » vit dans le menu de Profondeur, sur la ligne des angles (chantier
  // tete-du-projet) : la carte donne son geste à la feuille, et le reprend en partant.
  const { recentrer } = camera;
  useEffect(() => {
    onRecentrer?.(recentrer);
    return () => onRecentrer?.(null);
  }, [onRecentrer, recentrer]);
  const gestes = useGestesDeDepliage({
    profondeur,
    enfantsDe,
    ouvertes: ouvertesIci,
    visibles,
    // En colonnes, rien ne s'ouvre sur place (ni flèche ni molette) : la profondeur se choisit au sélecteur.
    actifs: !enDesignation && !enColonnes,
    places,
    placesFraiches,
    ouEst,
    centrerSur: camera.centrerSur,
  });
  molette.current = gestes.traiterLaMolette;

  // Combien d'étapes sont ouvertes, pour l'étape de la visite qui en fait ouvrir une.
  useEffect(() => signalerLesOuvertes(deplieesMontrees.size), [deplieesMontrees.size]);
  useEffect(() => () => signalerLesOuvertes(0), []);
  // L'importance que la carte fait grossir, une fois ses places arrivées (chantier `aide`, ticket 07).
  const grossit = filtreDessine?.importance ?? null;
  useEffect(() => signalerCeQueGrossitLaCarte(grossit), [grossit]);
  useEffect(() => () => signalerCeQueGrossitLaCarte(null), []);

  // P et L : les mêmes gestes que « + Post-it » et « + Liste », à la même place.
  useEffect(() => {
    if (!poseDesPapiers) return;
    poseDesPapiers.current =
      avecPapiers && !papiers.pose ? { postIt: papiers.poserUnPostIt, liste: papiers.poserUneListe } : null;
  });
  useEffect(
    () => () => {
      if (poseDesPapiers) poseDesPapiers.current = null;
    },
    [poseDesPapiers]
  );

  // Pour glisser les papiers (ticket 03) : la conversion de la caméra.
  const { versLaCarte } = camera;
  const carteDesPapiers = useMemo(() => ({ versLaCarte }), [versLaCarte]);

  /*
   * La couleur d'une bulle et de son trait : celle choisie pour sa branche, sinon son axe
   * automatique (chantier habit-des-bulles, modification 03) ; les dérivés (`--axe-fond`,
   * `--axe-bord`…) suivent `--axe`.
   */
  const axeDe = useCallback(
    (id: string) => {
      const bulle = parId.get(id);
      const choisie = bulle ? habitDe(bulle).couleur : null;
      return choisie ? teinteOuAccent(choisie) : couleurDAxe(carte.axes, id);
    },
    [carte.axes, habitDe, parId]
  );
  const teinte = teinteOuAccent(couleur);
  const { camera: regard } = camera;

  // La phrase du filtre : « 2 bulles urgentes sur 119, dans 2 branches ».
  const phrase = useMemo(() => {
    if (enAvant === null) return null;
    const { combien, branches } = compterLeFiltre(enfants, enAvant, carte.calque);
    return phraseDuFiltre(enAvant, combien, decompte.total, branches);
  }, [enAvant, enfants, carte.calque, decompte]);

  return (
    <div className="tableau-tout" style={{ ['--teinte' as string]: teinte }}>
      {/* Rien à régler tant qu'il n'y a rien : la planche de la feuille vide dessine la place
          à prendre, sans filtre ni profondeur. */}
      {enfants.length > 0 && (
        <BarreDeLaCarte
          enAvant={enAvant}
          onFiltrer={onFiltrer}
          phrase={phrase}
        />
      )}

      {/* Le fond de la carte, réglé dans l'habit du projet (chantier habit-du-projet, ticket 02,
          `fonds/papiers.css`) : son motif suit le glissement du regard, jamais son échelle. Un
          attribut plutôt qu'une classe : la caméra pose elle-même `tableau--tire` sur la surface.
          La forme de ses bulles aussi (ticket 03, `styles/bulles/`), et le trait de leur contour
          (ticket 04, `styles/bulles/trait*.css`). */}
      <div
        className={`tableau fond-de-projet forme-de-projet trait-de-projet${enLiaison ? ' tableau--lier' : ''}${
          cadenasOuvert && !enDesignation && !enColonnes ? ' tableau--deplacer' : ''
        }${glissee?.enMain ? ' tableau--glisse' : ''}`}
        data-fond={fond}
        data-forme={forme}
        data-trait={trait}
        data-direct={camera.direct ? 'oui' : undefined}
        style={regard ? { ['--fond-x' as string]: `${regard.x}px`, ['--fond-y' as string]: `${regard.y}px` } : undefined}
        ref={camera.surface}
        onPointerDown={(evenement) => {
          // Cadenas ouvert, un appui sur une bulle est pour elle ; sinon, pour le regard.
          if (!glisser.commencer(evenement)) camera.onPointerDown(evenement);
        }}
      >
        {/* Une feuille vide n'est pas une erreur : la planche y dessine la place à prendre. */}

        {regard && (
          <div
            ref={setMonde}
            className={`tableau-monde${camera.direct ? ' tableau-monde--direct' : ''}`}
            style={{
              transform: `translate(${regard.x}px, ${regard.y}px) scale(${regard.s})`,
              // La zone du doigt de la flèche garde sa taille à l'écran (pouce.css, ticket 15).
              ['--echelle' as string]: regard.s,
            }}
          >
            {/* Les papiers d'abord : sous les traits et les bulles, qui restent cliquables. */}
            {avecPapiers && <PapiersSurLaCarte papiers={papiers} carte={carteDesPapiers} />}

            {/* En colonnes, ni traits ni étiquettes (maquette `regroupe`) : les titres des colonnes. */}
            {enColonnes ? (
              enColonnes.titres.map((t) => (
                <div key={t.cle} className="tableau-colonne-titre" style={{ left: t.x, top: t.y }}>
                  {t.titre}
                </div>
              ))
            ) : (
              <>
                <TraitsCarte
                  bulles={bulles}
                  centreId={centre.id}
                  placeDe={ouEst}
                  visibles={visibles}
                  tailles={mesurees}
                  liens={liensVus}
                  etiquettes={etiquettes}
                  ouverteId={ouverteId}
                  axeDe={axeDe}
                  designe={designe?.lien.lienId ?? null}
                />

                <EtiquettesTransversales etiquettes={etiquettes} onOuvrir={enDesignation ? undefined : onFicheDAilleurs} />

                {/* Un clic sur un trait le désigne ; pas pendant qu'on hiérarchise ou qu'on lie. */}
                {!enDesignation && (
                  <ZonesDesLiens
                    liens={liensVus}
                    etiquettes={etiquettes}
                    placeDe={ouEst}
                    onDesigner={(lien, point) => setDesigne({ lien, point })}
                  />
                )}
              </>
            )}

            {/* La bulle du centre : on est déjà dedans, un clic ne fait rien, un double-clic
                ouvre sa fiche, comme sur toute bulle (retour 3 des testeurs, `gestesDuCentre`).
                Elle reste blanche, avec le filet de son dossier. */}
            <button
              type="button"
              className={`tableau-centre${enColonnes ? ' tableau-centre--cachee' : ''}`}
              aria-hidden={enColonnes ? true : undefined}
              tabIndex={enColonnes ? -1 : undefined}
              data-forme={forme}
              style={{ left: 0, top: 0 }}
              {...gestesDuCentre(() => {
                if (!enLiaison) onFiche(centre.id);
              })}
              title={GESTE_DU_CENTRE}
            >
              <span className="tableau-centre-teinte" aria-hidden="true" />
              <span>
                <span className="tableau-centre-titre">{centre.titre}</span>
                {/* L'avancement (décision d'Igor du 2026-10-03, « La Carte » 5). */}
                <span className="tableau-centre-detail">
                  {enfants.length === 0 ? RIEN_DEDANS : faitesSurN(decompte.faites, decompte.total)}
                  {/* En Analyse, le temps qui reste (theme-analyse, ticket 02, maquette `contenu`). */}
                  {enfants.length > 0 && loupe.analyse?.duCentre && ` · ${loupe.analyse.duCentre}`}
                </span>
              </span>
            </button>

            {bulles.map((bulle) => {
              const { x, y } = ouEst(bulle.id);
              const duPremier = bulle.parentId === centre.id;
              const filtre = dansLeCalque(bulle, enAvant, carte.calque);
              return (
                <NoeudCarte
                  key={bulle.id}
                  bulle={bulle}
                  x={x}
                  y={y}
                  axe={axeDe(bulle.id)}
                  habit={habitDe(bulle)}
                  habitDuProjet={habitDuProjet}
                  dedans={!duPremier}
                  ouverte={bulle.id === ouverteId}
                  depliee={!enColonnes && deplieesMontrees.has(bulle.id)}
                  depliable={depliable(bulle.id) && !enDesignation && !enColonnes}
                  rangEnAttente={duPremier ? (hierarchisation?.get(bulle.id) ?? null) : null}
                  premiere={lier?.premiere === bulle.id}
                  enHierarchisation={enDesignation}
                  cachee={!visibles.has(bulle.id)}
                  glissee={glissee?.id === bulle.id}
                  inerte={enHierarchisation && !duPremier}
                  taille={tailleAuFiltre(filtre.allumee, filtreDessine)}
                  combienDansLeCalque={enAvant && filtre.combien ? { priorite: enAvant, combien: filtre.combien } : null}
                  loupe={loupeSurLaBulle(loupe, bulle)}
                  onDescendre={lier ? lier.onClic : onDescendre}
                  onFiche={enLiaison ? rienAFaire : onFiche}
                  onEtat={enLiaison ? rienAFaire : onEtat}
                  onDeplier={enLiaison ? rienAFaire : gestes.basculer}
                  onRemettreASaPlace={
                    peutSeRemettreASaPlace(placesDuServeur[bulle.id], enColonnes !== null)
                      ? () => remettreASaPlace(bulle.id)
                      : null
                  }
                />
              );
            })}

            {designe && !enColonnes && !enDesignation && (
              <SignesDuLien key={designe.lien.lienId} designe={designe} onFermer={fermerLesSignes} />
            )}
          </div>
        )}

        {avecPapiers && !enLiaison && <AjoutDePapiers papiers={papiers} />}
      </div>
    </div>
  );
}
